import { AnalysisItem, AnalysisType, GroupType, projectAnalysis, ProjectAnalysisEvents } from "@/types/analysis/ProjectAnalysis";
import { ColorDefinition, DataVisualization } from "@/types/colors";
import { AppSettingType } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import BarChart, { BarChartApi } from "@components/Charts/BarChart";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import parseNumber from "@utils/parseNumber";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ResultsEvent, ResultsEvents } from "../../ResultsEvents";
import { getSystemNameById, getSubSystemNameById } from "@features/analysis/utils/metadataEntityUtils";
import { isolateViewersByAttribute } from "./viewerUtils";
import { useParams } from "react-router-dom";

export interface ByAttributesBarChartProps {
  width: number | undefined;
  height: number | undefined;
  emitter: Emitter<Record<EventType, unknown>>;
  attributeType: AnalysisType;
}

const ByAttributesBarChart = observer((props: ByAttributesBarChartProps) => {
  console.log("ByAttributesBarChart: ", props);
  const appStore = useAppStore();
  const cadModelCategories = useCadModelCategories();
  const { id: projectId } = useParams();
  const [chartColors, setChartColors] = useState<ColorDefinition[]>(DataVisualization.Colors.MaterialsPalette);
  const [chartData, setChartData] = useState([
    ["", ""],
    ["", 0],
  ]);
  const [hasChartData, setHasChartData] = useState(false);
  const [slantedText, setSlantedText] = useState<boolean>(false);
  const chartRef = useRef<BarChartApi | null>(null);

  useEffect(() => {
    updateChartData();
  }, []);

  useEffect(() => {
    updateChartData();
  }, [props.attributeType]);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateChartData);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateChartData);
    };
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateChartSelections);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateChartSelections);
    };
  }, []);

  useEffect(() => {
    props.emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);

    return () => {
      props.emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    };
  }, []);

  const handleUpdateSelection = async (event: any) => {
    if (event.key === "geometry-loaded") {
      updateViewers(event.groupKey);
    } else if (event.key !== "bar-chart") {
      updateSelection(event.groupKey);
    }
  };

  const updateChartSelections = async () => {
    const groupKeys = projectAnalysis.getSelections(props.attributeType);
    const analysisData = projectAnalysis.getAnalysis(props.attributeType);
    if (analysisData) {
      const legends = analysisData.getFilteredItems((item: AnalysisItem) => groupKeys.has(item.groupKey)).map((item: AnalysisItem) => item.name);
      chartRef.current?.setSelection({ legends });
    }
  };

  const updateSelection = (groupKey?: string) => {
    chartRef.current?.setSelection({ legends: groupKey });
  };

  const updateChartData = async () => {
    const analysisData = projectAnalysis.getAnalysis(props.attributeType);
    
    console.log('ByAttributesBarChart updateChartData:', {
      attributeType: props.attributeType,
      hasAnalysisData: !!analysisData,
      analysisDataKeys: analysisData ? Array.from(analysisData.scenarioAnalyses.keys()) : []
    });
    
    if (analysisData) {
      const xAxisTitle = projectAnalysis.isGroupTypeModel ? i18n.t("results-tab.title.x-axis.models") : i18n.t("results-tab.title.x-axis.scenarios");
      const keys = analysisData.getItemNames().map(name => {
        // Find the corresponding groupKey for this name to resolve the display name
        for (const scenarioAnalysis of analysisData.scenarioAnalyses.values()) {
          for (const groupData of scenarioAnalysis.groupData) {
            for (const item of groupData.items.values()) {
              if (item.name === name) {
                return resolveDisplayName(String(item.groupKey), props.attributeType);
              }
            }
          }
        }
        return name;
      });
      const newChartData = [];
      newChartData.push([xAxisTitle, ...keys]);
      
      analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
        scenarioAnalysis.groupData.forEach((groupData) => {
          newChartData.push([
            groupData.name,
            ...Array.from(groupData.items.values()).map((item: AnalysisItem) =>
              parseNumber(i18n.t("number", { value: item.co2eTotal, digits: AppSettingType.NumberFormat_EC_Digits }).replace(",", ""), i18n.language),
            ),
          ]);
        });
      });

      setChartColors(analysisData.colorDefinitions());
      setChartData(newChartData);
      setSlantedText(projectAnalysis.groupType === GroupType.Model);
      setHasChartData(true);
    } else {
      const xAxisTitle = projectAnalysis.isGroupTypeModel ? i18n.t("results-tab.title.x-axis.models") : i18n.t("results-tab.title.x-axis.scenarios");
      const newChartData: any = [
        [xAxisTitle, ""],
        ["", 0],
      ];
      setChartColors(DataVisualization.Colors.MaterialsPalette);
      setChartData(newChartData);
      setHasChartData(false);
    }
  };

  const getYAxisNameFromAttributeType = (attributeType: AnalysisType): string => {
    switch (attributeType) {
      case AnalysisType.System:
        return i18n.t("results-tab.title.y-axis.system");
      case AnalysisType.SubSystem:
        return i18n.t("results-tab.title.y-axis.sub-system");
      case AnalysisType.Attribute1:
        return i18n.t("results-tab.title.y-axis.attribute1");
      case AnalysisType.Attribute2:
        return i18n.t("results-tab.title.y-axis.attribute2");
      case AnalysisType.Attribute3:
        return i18n.t("results-tab.title.y-axis.attribute3");
      default:
        return i18n.t("results-tab.title.y-axis.system");
    }
  };

  const resolveDisplayName = (value: string, attributeType: AnalysisType): string => {
    if (attributeType === AnalysisType.System) {
      return getSystemNameById(appStore.metadataEntities, value) || value;
    }
    if (attributeType === AnalysisType.SubSystem) {
      return getSubSystemNameById(appStore.metadataEntities, value) || value;
    }
    return value;
  };

  const updateViewers = async (groupKey?: string | undefined): Promise<string | undefined> => {
    await isolateViewersByAttribute(groupKey, props.attributeType, cadModelCategories, appStore);
    return groupKey;
  };

  const handleChartSelection = async (_viewerIndex?: number | undefined, colIndex?: number | undefined, groupKey?: string | undefined) => {
    console.log(`updateViewers: ${colIndex}, ${groupKey}`);

    await updateViewers(groupKey);

    const event: ResultsEvent = {
      key: "bar-chart",
      eventType: ResultsEvents.SELECTION_CHANGED,
      groupKey,
    };

    props.emitter.emit(ResultsEvents.SELECTION_CHANGED, event);
  };

  const chartOptions = {
    title: i18n.t("barchart.title.header"),
    hAxis: {
      title: projectAnalysis.isGroupTypeModel ? i18n.t("results-tab.title.x-axis.models") : i18n.t("results-tab.title.x-axis.scenarios"),
    },
    vAxis: {
      title: getYAxisNameFromAttributeType(props.attributeType),
    },
  };

  return (
    <>
      {hasChartData && (
        <BarChart
          ref={chartRef}
          id={"bar-chart"}
          width={props.width}
          height={props.height}
          data={chartData}
          colors={chartColors}
          chartOptions={chartOptions}
          slantedText={slantedText}
          onSelect={(index, legend) => handleChartSelection(-1, index, legend)}
        />
      )}
    </>
  );
});

export default ByAttributesBarChart;
